import { writeOthersNameplates, writeOwnNameplate, type NameplateChoices, type OthersNameplates, type OwnNameplate, type SettingsStore } from "./settings.ts";

/**
 * The page's Nameplates choices (P1.9-T24; ADR-065, "the family is one look away"), as the world zoom's step is
 * the page's `WorldZoom`: made from the choices the store holds as the page loads, and taking each new choice
 * Settings makes, which it keeps per viewer. The choices live on a target the nameplates read every frame, the game
 * report's nameplates entry on the game page, so a choice applies to every plate on the next frame drawn, and a
 * plate is laid out again only when what it draws changes.
 *
 * @example
 * ```ts
 * const nameplates = new NameplateSettings(gameReport.nameplates, readNameplateChoices());
 * nameplates.setOthers("full");
 * ```
 */
export class NameplateSettings {
  /**
   * @param target Where the nameplates read the choices, given the choices the page is made with.
   * @param choices The choices the page is made with, as `readNameplateChoices` gives them.
   * @param store Where a new choice is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    private readonly target: NameplateChoices,
    choices: Readonly<NameplateChoices>,
    private readonly store?: SettingsStore | null,
  ) {
    target.others = choices.others;
    target.own = choices.own;
  }

  /**
   * What other players' plates draw now.
   *
   * @returns The choice.
   */
  get others(): OthersNameplates {
    return this.target.others;
  }

  /**
   * Whether the player's own plate draws now.
   *
   * @returns The choice.
   */
  get own(): OwnNameplate {
    return this.target.own;
  }

  /**
   * Takes the viewer's choice for other players' plates, which applies on the next frame, and keeps it.
   *
   * @param value The choice.
   */
  setOthers(value: OthersNameplates): void {
    this.target.others = value;
    writeOthersNameplates(value, this.store);
  }

  /**
   * Takes the viewer's choice for their own plate, which applies on the next frame, and keeps it.
   *
   * @param value The choice.
   */
  setOwn(value: OwnNameplate): void {
    this.target.own = value;
    writeOwnNameplate(value, this.store);
  }
}
